// Wire → view model for `/api/admin/vasp/*`.
//
// The seam every component reads through. Two rules it exists to enforce:
//
//   1. **Decimals and int64s stay strings.** `tokenQuantity`, `value` and
//      `fileSizeBytes` are carried through untouched — no `Number()`, no
//      `parseFloat`, not even a `?? 0`, because a coerced 0 is a real quantity
//      and an absent one is not. Absence is `null` (or `''` where the field is
//      non-nullable on the wire), and the formatter decides what to print.
//
//   2. **`status` is passed through verbatim.** It is vendor-controlled and
//      deliberately not an enum on either side, so the parser never maps,
//      normalises or defaults it. Rendering is
//      ../constants/vaspHoldingStatus.ts' job, and an unknown value there is
//      neutral, not an error.

import type {
  VaspBatchWebhookReplayDto,
  VaspDocumentReMirrorDto,
  VaspFailedWebhookEventsDto,
  VaspHoldingDto,
  VaspHoldingsDto,
  VaspMirroredDocumentDto,
  VaspTitleDeedDownloadDto,
  VaspWebhookEventDto,
  VaspWebhookEventPageDto,
  VaspWebhookReplayDto,
} from './vaspContracts'
import type {
  VaspBatchWebhookReplay,
  VaspDocumentReMirror,
  VaspFailedWebhookEvents,
  VaspHolding,
  VaspHoldings,
  VaspMirroredDocument,
  VaspTitleDeedDownload,
  VaspWebhookEvent,
  VaspWebhookReplay,
} from '../types/vaspTypes'
import type { PaginatedResponse } from '@shared/types/pagination'

export function parseVaspMirroredDocument(dto: VaspMirroredDocumentDto): VaspMirroredDocument {
  return {
    documentId: dto.documentId ?? '',
    fileName: dto.fileName ?? null,
    // Left as-is. A `?? 0` here would claim an unknown deed is zero bytes.
    fileSizeBytes: dto.fileSizeBytes ?? null,
    mimeType: dto.mimeType ?? null,
    issuedAt: dto.issuedAt ?? '',
    isDownloadable: dto.isDownloadable === true,
    retryCount: dto.retryCount ?? 0,
  }
}

export function parseVaspHolding(dto: VaspHoldingDto): VaspHolding {
  return {
    holdingId: dto.holdingId ?? '',
    organisationId: dto.organisationId ?? '',
    instrumentId: dto.instrumentId ?? '',
    listingId: dto.listingId ?? null,
    // Both decimals stay strings. `tokenQuantity` is non-nullable on the wire,
    // so an empty string is the only honest stand-in for a missing one — the
    // formatter renders that as its fallback dash, not as 0.
    tokenQuantity: dto.tokenQuantity ?? '',
    value: dto.value ?? null,
    currency: dto.currency ?? '',
    // Verbatim. See the header note.
    status: dto.status ?? '',
    recordedAt: dto.recordedAt ?? '',
    lastSyncedAt: dto.lastSyncedAt ?? null,
    orderId: dto.orderId ?? null,
    orderReference: dto.orderReference ?? null,
    investorId: dto.investorId ?? null,
    investorName: dto.investorName ?? null,
    investorEmail: dto.investorEmail ?? null,
    titleDeeds: (dto.titleDeeds ?? []).map(parseVaspMirroredDocument),
  }
}

export function parseVaspHoldings(dto: VaspHoldingsDto): VaspHoldings {
  const holdings = (dto?.holdings ?? []).map(parseVaspHolding)
  return {
    offeringId: dto?.offeringId ?? '',
    // Trust the server's count — it is the count before any client-side
    // slicing — but fall back to what actually arrived rather than to 0.
    totalHoldings: dto?.totalHoldings ?? holdings.length,
    holdings,
  }
}

/**
 * The presigned link.
 *
 * `url` is the one field with no sensible empty default: a blank string would
 * sail through to `window.open('')`, which opens a dead tab instead of saying
 * the server sent nothing usable. Guard at the seam, not in the caller — the
 * same shape `parseListingRequestDocumentDownload` uses.
 */
export function parseVaspTitleDeedDownload(
  dto: VaspTitleDeedDownloadDto | null | undefined,
): VaspTitleDeedDownload {
  const url = dto?.url ?? ''
  if (!url) throw new Error('The download link was missing from the server response.')
  return {
    url,
    expiresAt: dto?.expiresAt ?? '',
    fileName: dto?.fileName ?? '',
  }
}

export function parseVaspDocumentReMirror(dto: VaspDocumentReMirrorDto): VaspDocumentReMirror {
  return {
    holdingId: dto?.holdingId ?? '',
    attemptsBeforeRun: dto?.attemptsBeforeRun ?? 0,
    retryCount: dto?.retryCount ?? 0,
    forced: dto?.forced === true,
    titleDeeds: (dto?.titleDeeds ?? []).map(parseVaspMirroredDocument),
  }
}

export function parseVaspWebhookEvent(dto: VaspWebhookEventDto): VaspWebhookEvent {
  return {
    id: dto.id ?? '',
    eventId: dto.eventId ?? '',
    eventType: dto.eventType ?? '',
    entityType: dto.entityType ?? null,
    entityId: dto.entityId ?? null,
    status: dto.status ?? '',
    failedReason: dto.failedReason ?? null,
    retryCount: dto.retryCount ?? 0,
    receivedAt: dto.receivedAt ?? '',
    processedAt: dto.processedAt ?? null,
    isReplayable: dto.isReplayable === true,
  }
}

/**
 * One page of the event log → the canonical PaginatedResponse shape every
 * listing in this app converges on. `totalPages` is synthesized when the
 * backend omits it, per shared/types/pagination.ts.
 */
export function parseVaspWebhookEventPage(
  dto: VaspWebhookEventPageDto,
): PaginatedResponse<VaspWebhookEvent> {
  const data = (dto?.data ?? []).map(parseVaspWebhookEvent)
  const limit = dto?.meta?.limit ?? data.length
  const totalItems = dto?.meta?.total ?? data.length
  return {
    data,
    pagination: {
      page: dto?.meta?.page ?? 1,
      limit,
      totalItems,
      totalPages: dto?.meta?.totalPages ?? (limit > 0 ? Math.ceil(totalItems / limit) : 0),
    },
  }
}

export function parseVaspFailedWebhookEvents(
  dto: VaspFailedWebhookEventsDto,
): VaspFailedWebhookEvents {
  const events = (dto?.events ?? []).map(parseVaspWebhookEvent)
  return {
    totalEvents: dto?.totalEvents ?? events.length,
    events,
  }
}

export function parseVaspWebhookReplay(dto: VaspWebhookReplayDto): VaspWebhookReplay {
  return {
    id: dto.id ?? '',
    eventId: dto.eventId ?? '',
    eventType: dto.eventType ?? '',
    statusBefore: dto.statusBefore ?? '',
    statusAfter: dto.statusAfter ?? '',
    retryCount: dto.retryCount ?? 0,
    // `=== true` rather than truthiness: an absent flag must not read as
    // dispatched. The whole outcome grammar hangs off this one boolean.
    isDispatched: dto.isDispatched === true,
    forced: dto.forced === true,
    refusedReason: dto.refusedReason ?? null,
    failedReason: dto.failedReason ?? null,
  }
}

export function parseVaspBatchWebhookReplay(
  dto: VaspBatchWebhookReplayDto,
): VaspBatchWebhookReplay {
  return {
    requestedLimit: dto?.requestedLimit ?? 0,
    candidateCount: dto?.candidateCount ?? 0,
    processedCount: dto?.processedCount ?? 0,
    refusedCount: dto?.refusedCount ?? 0,
    failedCount: dto?.failedCount ?? 0,
    outcomes: (dto?.outcomes ?? []).map(parseVaspWebhookReplay),
  }
}
